import { motion } from "motion/react";
import React, { useMemo } from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import MyOwnCarIcon from "../../../image/icons/itinerary/MyOwnCarIcon.svg?react";
import DestinationIcon from "../../../image/icons/map/details/DestinationIcon.svg";
import { fabUiSlice, usePersistedSliceSelector } from "../../../store/persistence";
import { formatTime } from "../../../tools/TimeRelated";
import { getItineraryLegColorRgba } from "../../../tools/TransportModeColorsTools";
import {
    getTransportModeGroup,
    getTransportModeGroupForItineraryLeg,
    isDurationOnlyTransportMode,
    isSharedMobilityItineraryLeg,
    isTransportModeInGroups,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type TravelProgressProps } from "../../../types/ItineraryPlanner";
import {
    buildTravelProgressLegSegmentMarkerMap,
    getTravelProgressLegSegmentMarkerStyle,
} from "../../Debug/itineraryDebug";
import {
    isUiMotionEnabled,
    resolveUiItineraryPanelProgressDelay,
    resolveUiSpring,
} from "../../shared/motion/uiMotion.tools";
import { getLegRouteShortName } from "../itineraryDetails/itineraryDetails.tools";
import ItineraryTransportIcon from "../shared/ItineraryTransportIcon";

const TRAVEL_PROGRESS_LEG_STRIP_OPACITY_DEFAULT = 0.2;

/** Walk, bike, and rental strips need higher opacity on glass card backgrounds. */
const TRAVEL_PROGRESS_LEG_STRIP_OPACITY_SOFT_MODES = 0.45;

/**
 * Resolves leg strip fill opacity for list/detail progress bars.
 *
 * @param segment Itinerary leg used to resolve the transport mode group.
 * @returns Alpha value in `[0, 1]` for the strip background color.
 */
const resolveTravelProgressLegStripOpacity = ( segment: ItineraryLegDTO ): number => {
    const modeGroup = getTransportModeGroupForItineraryLeg( segment );

    if (
        modeGroup === TransportModeGroup.WALK ||
        modeGroup === TransportModeGroup.BICYCLE ||
        modeGroup === TransportModeGroup.RENTAL
    ) {
        return TRAVEL_PROGRESS_LEG_STRIP_OPACITY_SOFT_MODES;
    }

    return TRAVEL_PROGRESS_LEG_STRIP_OPACITY_DEFAULT;
};

/** Minimum strip width (px) for every leg so short segments stay visible with duration-proportional flex. */
const MIN_LEG_PROGRESS_WIDTH_PX = 80;
const CARPOOL_PLACEHOLDER_BADGE_SIZE_PX = 35;

/** Short walking hops between vehicles are hidden; they must not affect duration totals. */
const shouldOmitLegFromProgressBar = (
    segment: ItineraryLegDTO,
    legIndex:                     number,
    totalLegCount:                number,
): boolean => {
    const modeGroup = getTransportModeGroup( segment.mode );

    return (
        modeGroup === TransportModeGroup.WALK &&
        legIndex < totalLegCount - 1 &&
        segment.duration < 240
    );
};

const segmentDurationSeconds = ( segment: ItineraryLegDTO ): number => {
    const raw = segment.duration;

    if ( typeof raw !== "number" || Number.isNaN( raw ) || raw < 0 ) {
        return 0;
    }

    return raw;
};

/** Narrow rental vehicle type for icons without relying on chains that confuse strict lint when TS has path conflicts. */
const rentalVehicleTypeForTripIcon = ( leg: ItineraryLegDTO ): string | undefined => {
    const rentalUnknown: unknown = ( leg as { "rental"?: unknown } )["rental"];

    if ( typeof rentalUnknown !== "object" || rentalUnknown === null || !( "vehicle" in rentalUnknown ) ) {
        return undefined;
    }

    const typeUnknown: unknown = ( rentalUnknown as { "vehicle"?: { "type"?: unknown } } )["vehicle"]?.["type"];

    return typeof typeUnknown === "string" ? typeUnknown : undefined;
};

interface TravelProgressLegStripProps {

    /** Whether the strip should play the post-search grow animation. */
    "animateSegmentReveal": boolean;

    /** Background color for the leg strip. */
    "backgroundColor"?: string;

    /** Extra Tailwind classes for the strip container. */
    "className"?: string;

    /** Itinerary row index for reveal timing offsets. */
    "itineraryRowIndex": number;

    /** Strip inner content (duration label, car label, spinner, etc.). */
    "children"?: React.ReactNode;

    /** Visible leg index within the row for left-to-right stagger. */
    "segmentRevealIndex": number;
}

/**
 * Colored duration strip for one leg in {@link TravelProgress}.
 *
 * @param props Leg strip props.
 * @returns Static or animated leg strip.
 */
const TravelProgressLegStrip: React.FC<TravelProgressLegStripProps> = ( {
    animateSegmentReveal,
    backgroundColor,
    children,
    className = "",
    itineraryRowIndex,
    segmentRevealIndex,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const stripClassName = `-z-10 h-7 flex min-w-0 flex-1 justify-center items-center -ml-2 -mr-2 w-full ${ className }`;
    const stripStyle: React.CSSProperties = { "transformOrigin": "left center" };

    if ( backgroundColor !== undefined ) {
        stripStyle.backgroundColor = backgroundColor;
    }

    const segmentRevealDelay: number = resolveUiItineraryPanelProgressDelay(
        segmentRevealIndex,
        itineraryRowIndex,
        reducedMotion,
    );

    if ( !animationsEnabled || !animateSegmentReveal ) {
        return (
            <div
                className={stripClassName}
                style={stripStyle}
            >
                {children}
            </div>
        );
    }

    return (
        <motion.div
            animate={{
                "opacity": 1,
                "scaleX":  1,
            }}
            className={stripClassName}
            initial={{
                "opacity": 0.55,
                "scaleX":  0,
            }}
            style={stripStyle}
            transition={{
                ...resolveUiSpring( reducedMotion, "gentle" ),
                "delay": segmentRevealDelay,
            }}
        >
            {children}
        </motion.div>
    );
};

const TravelProgress: React.FC<TravelProgressProps> = ( {
    animateSegmentReveal = false,
    isCarpoolIncomplete,
    isMyCar,
    itineraryRowIndex = 0,
    legs,
    pricingSegments,
} ) => {
    const debugItineraries = usePersistedSliceSelector( fabUiSlice, ( state ) => state.debugItineraries ) === true;
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const allLegs = legs ?? [];
    const totalLegCount = allLegs.length;

    const totalVisibleDuration = allLegs.reduce( ( sum, segment, index ) => {
        if ( shouldOmitLegFromProgressBar( segment, index, totalLegCount ) ) {
            return sum;
        }

        return sum + segmentDurationSeconds( segment );
    }, 0 );

    const progressRevealPlan = useMemo( () => {
        type TravelProgressRevealEntry = {
            "kind":        "carpool";
            "keySuffix":   string;
            "revealIndex": number;
        } | {
            "kind":        "leg";
            "legIndex":    number;
            "revealIndex": number;
            "segment":     ItineraryLegDTO;
        };

        const legList = legs ?? [];
        const legCount = legList.length;
        const entries: TravelProgressRevealEntry[] = [];
        let revealIndex = 0;

        if ( isCarpoolIncomplete === true ) {
            entries.push( {
                "kind":        "carpool",
                "keySuffix":   "leading",
                "revealIndex": revealIndex,
            } );
            revealIndex += 1;
        }

        legList.forEach( ( segment, legIndex ) => {
            if ( shouldOmitLegFromProgressBar( segment, legIndex, legCount ) ) {
                return;
            }

            entries.push( {
                "kind":        "leg",
                "legIndex":    legIndex,
                "revealIndex": revealIndex,
                segment,
            } );
            revealIndex += 1;
        } );

        if ( isCarpoolIncomplete === true ) {
            entries.push( {
                "kind":        "carpool",
                "keySuffix":   "trailing",
                "revealIndex": revealIndex,
            } );
            revealIndex += 1;
        }

        return {
            "destinationRevealIndex": revealIndex,
            entries,
        };
    }, [ isCarpoolIncomplete, legs ] );

    const compactSegmentMarkerMap = useMemo( () => {
        const visibleLegIndexes: number[] = [];
        for ( const entry of progressRevealPlan.entries ) {
            if ( entry.kind === "leg" ) {
                visibleLegIndexes.push( entry.legIndex );
            }
        }

        return buildTravelProgressLegSegmentMarkerMap( pricingSegments, visibleLegIndexes );
    }, [ pricingSegments, progressRevealPlan.entries ] );

    const destinationRevealDelay: number = resolveUiItineraryPanelProgressDelay(
        progressRevealPlan.destinationRevealIndex,
        itineraryRowIndex,
        reducedMotion,
    );

    const renderCarpoolPlaceholder = ( keySuffix: string, revealIndex: number ): React.ReactNode => (
        <div
            aria-label="Segment en attente de completion"
            className="relative flex items-center shrink-0"
            key={`carpool-placeholder-${ keySuffix }`}
            style={{ "minWidth": `${ MIN_LEG_PROGRESS_WIDTH_PX }px` }}
        >
            <div className="relative shrink-0 z-10">
                <span
                    className="inline-flex items-center justify-center rounded-full border-[1.5px] border-white shrink-0 text-[17px] font-bold leading-none"
                    style={{
                        "width":           `${ CARPOOL_PLACEHOLDER_BADGE_SIZE_PX }px`,
                        "height":          `${ CARPOOL_PLACEHOLDER_BADGE_SIZE_PX }px`,
                        "backgroundColor": "#94a3b8",
                        "color":           "#ffffff",
                    }}
                >
                    ?
                </span>
            </div>
            <TravelProgressLegStrip
                animateSegmentReveal={animateSegmentReveal}
                backgroundColor="rgba(148, 163, 184, 0.35)"
                className="rounded-sm -ml-2"
                itineraryRowIndex={itineraryRowIndex}
                segmentRevealIndex={revealIndex}
            >
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent text-[12px]" />
            </TravelProgressLegStrip>
        </div>
    );

    return (
        <div
            className="flex w-full min-w-0 flex-row items-center overflow-y-hidden overflow-x-auto"
            id="TravelProgress"
            style={{ "padding": "0px 0px 8px 0px" }}
        >
            {progressRevealPlan.entries.map( ( entry ) => {
                if ( entry.kind === "carpool" ) {
                    return renderCarpoolPlaceholder( entry.keySuffix, entry.revealIndex );
                }

                const segment = entry.segment;
                const modeGroup = getTransportModeGroupForItineraryLeg( segment );
                const routeShortName = getLegRouteShortName( segment );
                const durationPart = segmentDurationSeconds( segment );
                const shouldShowCarLegDuration = isMyCar !== true && isTransportModeInGroups( segment.mode, [
                    TransportModeGroup.CAR,
                    TransportModeGroup.MY_CAR,
                ] );
                const shouldShowLegDuration =
                    isDurationOnlyTransportMode( segment.mode ) ||
                    isSharedMobilityItineraryLeg( segment ) ||
                    shouldShowCarLegDuration;
                const flexStyle: React.CSSProperties =
                  totalVisibleDuration > 0
                      ? {
                          "flexBasis":  0,
                          "flexGrow":   durationPart,
                          "flexShrink": 1,
                          "minWidth":   MIN_LEG_PROGRESS_WIDTH_PX,
                      }
                      : {
                          "flexBasis":  0,
                          "flexGrow":   1,
                          "flexShrink": 1,
                          "minWidth":   MIN_LEG_PROGRESS_WIDTH_PX,
                      };
                const segmentMarkerStyle = getTravelProgressLegSegmentMarkerStyle(
                    debugItineraries,
                    compactSegmentMarkerMap,
                    entry.legIndex,
                );

                return (
                    <div
                        className="relative flex items-center"
                        key={segment.id}
                        style={flexStyle}
                    >
                        <div className="relative shrink-0">
                            {isMyCar === true && modeGroup === TransportModeGroup.MY_CAR
                                ? (
                                    <MyOwnCarIcon
                                        aria-label="MyCar"
                                        style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                                    />
                                )
                                : ItineraryTransportIcon(
                                    segment.mode,
                                    false,
                                    "badge",
                                    rentalVehicleTypeForTripIcon( segment ),
                                    segment,
                                )}
                            {!isTransportModeInGroups( segment.mode, [
                                TransportModeGroup.WALK,
                                TransportModeGroup.BICYCLE,
                                TransportModeGroup.RENTAL,
                                TransportModeGroup.BLABLADAILY,
                                TransportModeGroup.BLABLACAR,
                                TransportModeGroup.HE_LEMAN,
                            ] ) &&
                            segment.from?.stop?.id != null &&
                            segment.from?.stop?.id !== undefined && (
                                <p
                                    className="min-w-8 justify-center flex items-center font-bold bg-ionic-primary text-white absolute -bottom-2 -right-3 p-1 rounded-md h-4 border border-white"
                                    style={{ "fontSize": "10px" }}
                                >
                                    {routeShortName.slice( 0, 5 )}
                                </p>
                            )}
                        </div>
                        <TravelProgressLegStrip
                            animateSegmentReveal={animateSegmentReveal}
                            backgroundColor={isMyCar === true
                                ? undefined
                                : getItineraryLegColorRgba( segment, resolveTravelProgressLegStripOpacity( segment ) )}
                            className={isMyCar === true ? "bg-gray-my-car bg-opacity-25" : ""}
                            itineraryRowIndex={itineraryRowIndex}
                            segmentRevealIndex={entry.revealIndex}
                        >
                            <div
                                className="flex w-full items-center justify-center"
                                style={segmentMarkerStyle}
                            >
                                {isMyCar === true
                                    ? (
                                        <span
                                            className="text-[14px] font-bold"
                                            style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                                        >
                                            Je prends ma voiture
                                        </span>
                                    )
                                    : null}
                                {segment.from?.stop?.id != null &&
                                  segment.from?.stop?.id !== undefined &&
                                  !isTransportModeInGroups( segment.mode, [
                                        TransportModeGroup.WALK,
                                        TransportModeGroup.BICYCLE,
                                        TransportModeGroup.RENTAL,
                                        TransportModeGroup.CAR,
                                        TransportModeGroup.MY_CAR,
                                    ] )
                                    ? (
                                        <span
                                            className="text-[12px]"
                                            style={{ "color": "var(--itinerary-text-primary, #0f172a)" }}
                                        >
                                            {formatTime( segment.duration, true )}
                                        </span>
                                    )
                                    : null}
                                {shouldShowLegDuration
                                    ? (
                                        <span
                                            className="text-[12px]"
                                            style={{ "color": "var(--itinerary-text-primary, #0f172a)" }}
                                        >
                                            {formatTime( segment.duration, true )}
                                        </span>
                                    )
                                    : null}
                            </div>
                        </TravelProgressLegStrip>
                    </div>
                );
            } )}
            {animationsEnabled && animateSegmentReveal
                ? (
                    <motion.img
                        alt="ArrivalPoint"
                        animate={{
                            "opacity": 1,
                            "scale":   1,
                        }}
                        className="shrink-0"
                        initial={{
                            "opacity": 0,
                            "scale":   0.85,
                        }}
                        src={DestinationIcon}
                        style={{ "height": "35px" }}
                        transition={{
                            ...resolveUiSpring( reducedMotion, "gentle" ),
                            "delay": destinationRevealDelay,
                        }}
                    />
                )
                : (
                    <img
                        alt="ArrivalPoint"
                        className="shrink-0"
                        src={DestinationIcon}
                        style={{ "height": "35px" }}
                    />
                )}
        </div>
    );
};

export default TravelProgress;
